import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { LandingPage } from './pages/LandingPage'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import { LoadingSpinner } from './components/ui/LoadingSpinner'
import { Layout } from './components/Layout'
import { Toaster } from 'react-hot-toast'

// Lazy-loaded pages — only loaded when the user navigates to them
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const ClientsPage = lazy(() => import('./pages/ClientsPage').then(m => ({ default: m.ClientsPage })))
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })))
const AssessmentDetailPage = lazy(() => import('./pages/AssessmentDetailPage').then(m => ({ default: m.AssessmentDetailPage })))
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })))
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })))
const ResourcesPage = lazy(() => import('./pages/ResourcesPage').then(m => ({ default: m.ResourcesPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const BlogListPage = lazy(() => import('./pages/BlogListPage').then(m => ({ default: m.BlogListPage })))
const ToolsDashboardPage = lazy(() => import('./pages/ToolsDashboardPage'))
const BlogPostPage = lazy(() => import('./pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })))
const ComparisonPage = lazy(() => import('./pages/ComparisonPage').then(m => ({ default: m.ComparisonPage })))
const CmmcForConsultantsPage = lazy(() => import('./pages/CmmcForConsultantsPage').then(m => ({ default: m.CmmcForConsultantsPage })))
const ScanDashboardPage = lazy(() => import('./pages/ScanDashboardPage').then(m => ({ default: m.ScanDashboardPage })))
const ScanImportPage = lazy(() => import('./pages/ScanImportPage').then(m => ({ default: m.ScanImportPage })))
const FeaturesPage = lazy(() => import('./pages/FeaturesPage').then(m => ({ default: m.FeaturesPage })))
const ConfigScannerPage = lazy(() => import('./pages/ConfigScannerPage').then(m => ({ default: m.ConfigScannerPage })))
const EvidenceManagerPage = lazy(() => import('./pages/EvidenceManagerPage').then(m => ({ default: m.EvidenceManagerPage })))
const ReviewQueuePage = lazy(() => import('./pages/ReviewQueuePage').then(m => ({ default: m.ReviewQueuePage })))
const OutreachPage = lazy(() => import('./pages/OutreachPage').then(m => ({ default: m.OutreachPage })))
const LeadDetailPage = lazy(() => import('./pages/LeadDetailPage').then(m => ({ default: m.LeadDetailPage })))
const ChecklistPage = lazy(() => import('./pages/ChecklistPage').then(m => ({ default: m.ChecklistPage })))
const ReadinessScorePage = lazy(() => import('./pages/ReadinessScorePage').then(m => ({ default: m.ReadinessScorePage })))
const FindConsultantPage = lazy(() => import('./pages/FindConsultantPage').then(m => ({ default: m.FindConsultantPage })))
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })))
const SSPGeneratorPage = lazy(() => import('./pages/SSPGeneratorPage').then(m => ({ default: m.SSPGeneratorPage })))
const ReadinessPage = lazy(() => import('./pages/ReadinessPage').then(m => ({ default: m.ReadinessPage })))
const PolicyTemplatesPage = lazy(() => import('./pages/PolicyTemplatesPage').then(m => ({ default: m.PolicyTemplatesPage })))
const MonitoringDriftTimelinePage = lazy(() => import('./pages/MonitoringDriftTimelinePage').then(m => ({ default: m.MonitoringDriftTimelinePage })))
const ComplianceTrendDashboard = lazy(() => import('./pages/ComplianceTrendDashboard').then(m => ({ default: m.ComplianceTrendDashboard })))
const EmailInquiriesPage = lazy(() => import('./pages/EmailInquiriesPage').then(m => ({ default: m.EmailInquiriesPage })))
const ReadinessCalculatorPage = lazy(() => import('./pages/ReadinessCalculatorPage').then(m => ({ default: m.ReadinessCalculatorPage })))
const MonitoringConfigPage = lazy(() => import('./pages/MonitoringConfigPage').then(m => ({ default: m.MonitoringConfigPage })))
const AuditLogPage = lazy(() => import('./pages/AuditLogPage').then(m => ({ default: m.AuditLogPage })))
const ContractorDashboardPage = lazy(() => import('./pages/ContractorDashboardPage').then(m => ({ default: m.ContractorDashboardPage })))
const OnboardingWizardPage = lazy(() => import('./pages/OnboardingWizardPage').then(m => ({ default: m.OnboardingWizardPage })))
const OnboardingScopingPage = lazy(() => import('./pages/OnboardingScopingPage').then(m => ({ default: m.OnboardingScopingPage })))
const FeedbackDashboardPage = lazy(() => import('./pages/FeedbackDashboardPage').then(m => ({ default: m.FeedbackDashboardPage })))
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))

/** Page loading fallback */
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}

/** Auth-gated routes that use the Layout */
function AuthenticatedRoutes() {
  return (
    <Layout>
      <Toaster position="bottom-right" />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/assessments/:id" element={<AssessmentDetailPage />} />
          <Route path="/assessments/:id/scan-import" element={<ScanImportPage />} />
          <Route path="/assessments/:id/evidence" element={<EvidenceManagerPage />} />
          <Route path="/review" element={<ReviewQueuePage />} />
          <Route path="/outreach" element={<OutreachPage />} />
          <Route path="/outreach/:id" element={<LeadDetailPage />} />
          <Route path="/assessments/:id/ssp" element={<SSPGeneratorPage />} />
          <Route path="/assessments/:id/readiness" element={<ReadinessPage />} />
          <Route path="/assessments/:id/monitoring/timeline" element={<MonitoringDriftTimelinePage />} />
          <Route path="/assessments/:id/monitoring/trend" element={<ComplianceTrendDashboard />} />
          <Route path="/assessments/:id/monitoring/config" element={<MonitoringConfigPage />} />
          <Route path="/scan-import" element={<ScanImportPage />} />
          <Route path="/config-scanner" element={<ConfigScannerPage />} />
          <Route path="/policy-templates" element={<PolicyTemplatesPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/email-inquiries" element={<EmailInquiriesPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/audit-logs" element={<AuditLogPage />} />
          <Route path="/scans" element={<ScanDashboardPage />} />
          <Route path="/tools" element={<ToolsDashboardPage />} />
          <Route path="/feedback" element={<FeedbackDashboardPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/cmmc-checklist" element={<ChecklistPage />} />
        <Route path="/sprs-calculator" element={<ReadinessCalculatorPage />} />
          <Route path="/cmmc-readiness" element={<ReadinessScorePage />} />
          <Route path="/find-consultant" element={<FindConsultantPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/vs-futurefeed" element={<ComparisonPage slug="vs-futurefeed" />} />
          <Route path="/vs-cyturus" element={<ComparisonPage slug="vs-cyturus" />} />
          <Route path="/vs-spreadsheets" element={<ComparisonPage slug="vs-spreadsheets" />} />
          {/* Retired: redirected in ComparisonPage so inbound links survive. */}
          <Route path="/vs-drata" element={<ComparisonPage slug="vs-drata" />} />
          <Route path="/vs-vanta" element={<ComparisonPage slug="vs-vanta" />} />
          <Route path="/contractor/dashboard" element={<ContractorDashboardPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}

/** App.
 *
 * Route-based code splitting via React.lazy. Only the initial page
 * (landing/login/register) loads synchronously; all other pages
 * are loaded on demand.
 */
function App() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/cmmc-for-consultants" element={<CmmcForConsultantsPage />} />
        {/* The Security Bundle is no longer sold (ADR-0029); inbound links land on the one price list. */}
        <Route path="/security-bundle" element={<Navigate to="/pricing" replace />} />
        <Route path="/cmmc-checklist" element={<ChecklistPage />} />
        <Route path="/sprs-calculator" element={<ReadinessCalculatorPage />} />
        <Route path="/cmmc-readiness" element={<ReadinessScorePage />} />
        <Route path="/find-consultant" element={<FindConsultantPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/vs-futurefeed" element={<ComparisonPage slug="vs-futurefeed" />} />
        <Route path="/vs-cyturus" element={<ComparisonPage slug="vs-cyturus" />} />
        <Route path="/vs-spreadsheets" element={<ComparisonPage slug="vs-spreadsheets" />} />
        {/* Retired: redirected in ComparisonPage so inbound links survive. */}
        <Route path="/vs-drata" element={<ComparisonPage slug="vs-drata" />} />
        <Route path="/vs-vanta" element={<ComparisonPage slug="vs-vanta" />} />
        <Route path="/contractor/register" element={<OnboardingWizardPage />} />
        <Route path="/onboarding/scoping" element={<OnboardingScopingPage />} />
        <Route path="/contractor/dashboard" element={<ContractorDashboardPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/tos" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // Logged in but email not yet verified (bypass if verification disabled or flagged in local storage)
  if (user.emailVerified === false && import.meta.env.VITE_DISABLE_EMAIL_VERIFICATION !== 'true') {
    return (
      <>
        <Toaster position="bottom-right" />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/contractor/dashboard" element={<ContractorDashboardPage />} />
            <Route path="*" element={<Navigate to="/verify-email" replace />} />
          </Routes>
        </Suspense>
      </>
    )
  }

  return <AuthenticatedRoutes />
}

export default App
